import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

/**
 * True inside a frame when any page above us is another origin. Same-origin frames (our own test
 * pages) do not count. Where the browser exposes the ancestor chain every ancestor is checked;
 * otherwise the top page is, so a nested same-origin frame under a foreign top still counts.
 */
export const framedByForeignOrigin = (win: Window = window): boolean => {
	if (win.self === win.top) {
		return false;
	}
	const ancestors = win.location.ancestorOrigins;
	if (ancestors && ancestors.length > 0) {
		// Chromium and WebKit: every frame above us must be ours, a foreign middle frame counts too.
		return Array.from(ancestors).some(origin => origin !== win.location.origin);
	}
	try {
		return win.top.location.origin !== win.location.origin;
	} catch {
		// Reading a cross-origin top throws, which is exactly the foreign case.
		return true;
	}
};

/**
 * Froda Native routes refuse to be framed (frame-ancestors 'self', TE-6038), but that header
 * only applies when the browser loads a document. A frameable white-label page can still move
 * into a native route with the client-side router, for example /p/<partner>/login?returnUrl=…
 * after an in-frame login, or through a history entry the embedding page replays. This guard
 * turns any such later navigation into a full page load, so the header decides.
 *
 * It lets the document load through (first navigation to the loaded path), where the header
 * already had its say, and skipLocationChange hops: the white-label company switch passes
 * through '/' to reset router state, and nothing outside the app's own code can start such a
 * navigation. Everything else, redirects during the first navigation included, becomes a full load.
 */
/** Path the document was loaded with. The first navigation may only end there. */
const documentPath = window.location.pathname;

const pathOf = (url: string): string => url.split(/[?#]/)[0];

export const notFramedGuard: CanActivateFn = (_route, state) => {
	if (!framedByForeignOrigin()) {
		return true;
	}
	const router = inject(Router);
	if (router.getCurrentNavigation()?.extras.skipLocationChange) {
		return true;
	}
	// The document load itself: the header already decided it, and forcing a reload would loop
	// wherever the header is absent. A redirect inside that first navigation to another path
	// (a redirectTo or a guard) is not the document load and gets the full-load treatment.
	if (!router.navigated && pathOf(state.url) === documentPath) {
		return true;
	}
	window.location.assign(state.url);
	return false;
};
